import { motion } from 'framer-motion';

/**
 * EngagedlyEmpathyMap, a companion to `CloudHubEmpathyMap` that uses the
 * same premium visual language (radial gradient background, blueprint grid,
 * paper-plane decoration, 4-quadrant grid around a central persona) but
 * tuned for the Engagedly Homepage Redesign case study.
 *
 *  • Palette is emerald / violet / rose / sky (Engagedly's warmer HR-tech
 *    tone) instead of CloudHub's enterprise blue.
 *  • Central persona is Wendy Horton, the Sales Associate who came out of
 *    the co-design workshop.
 *  • Copy is drawn from the workshop's Problems / Statements / Expectations
 *    board, framed as Says / Thinks / Does / Feels for the standard four
 *    empathy-map quadrants.
 */

const easeOut = [0.2, 0.8, 0.2, 1];

const empathy = {
    says: {
        label: 'Says',
        items: [
            'The home page is where I get lost most days.',
            'I wish I only saw the widgets that matter to me.',
            'Announcements feel stale by the time I read them.',
            'Give me my tasks and my team \u2014 not everyone\u2019s.',
        ],
    },
    thinks: {
        label: 'Thinks',
        items: [
            'There has to be a way to make this feel like MY page.',
            'Why is the same content here every time I visit?',
            'I never know where to look first.',
            'The old intranet had more character than this.',
        ],
    },
    does: {
        label: 'Does',
        items: [
            'Bookmarks her dashboard and skips the home page.',
            'Skims the top strip, scrolls past everything else.',
            'Manually navigates to CRM and marketing tools.',
            'Asks teammates on chat instead of the portal.',
        ],
    },
    feels: {
        label: 'Feels',
        items: [
            'Frustrated by clutter and yesterday\u2019s news.',
            'A personalised page would feel like a warm handshake.',
            'Small wins visible here would be motivating.',
            'Wants to feel that the company gets who she is.',
        ],
    },
};

// Wendy portrait, already referenced by the persona block in this study.
const WENDY_AVATAR = 'https://framerusercontent.com/images/OpXHuB9pQ5m9wRlRbQ1YgQXJm04.png';

const CornerNote = ({ q, align }) => (
    <div
        className={`flex flex-col gap-3 ${
            align === 'right' ? 'items-end text-right' : 'items-start'
        }`}
    >
        <span className="font-display text-4xl md:text-5xl text-white/90 leading-none">
            {q.label}
        </span>
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-4%' }}
            transition={{ duration: 0.55, ease: easeOut }}
            className="w-full max-w-[340px] rounded-2xl border p-4 md:p-5 backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            style={{
                background: 'rgba(76, 29, 149, 0.55)',
                borderColor: 'rgba(255, 255, 255, 0.22)',
            }}
        >
            <ul className="flex flex-col gap-1.5">
                {q.items.map((it, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2 font-sans-brand text-[12.5px] md:text-[13px] leading-[1.5] text-white/95 text-left"
                    >
                        <span className="mt-[7px] w-1 h-1 rounded-full bg-white/70 shrink-0" />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </motion.article>
    </div>
);

// A tiny animated paper plane decoration, matches the CloudHub map.
const PaperPlane = ({ className, rotate = 0 }) => (
    <svg
        viewBox="0 0 60 40"
        className={`absolute pointer-events-none ${className}`}
        aria-hidden
        style={{ transform: `rotate(${rotate}deg)` }}
    >
        <path
            d="M2 20 L58 4 L34 38 L28 24 Z"
            fill="rgba(255,255,255,0.85)"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="0.6"
        />
        <path
            d="M2 20 L28 24 L34 38"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="0.6"
            fill="none"
        />
        <path
            d="M2 20 C -20 30, -32 12, -50 22"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="0.9"
            strokeDasharray="2 3"
            fill="none"
        />
    </svg>
);

export const EngagedlyEmpathyMap = () => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.7, ease: easeOut }}
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] overflow-hidden"
        data-testid="engagedly-empathy-map"
        style={{
            background:
                'radial-gradient(120% 100% at 50% 40%, #6d28d9 0%, #4c1d95 45%, #2e1065 80%, #1e0a4c 100%)',
        }}
    >
        <header className="p-5 md:p-7 pb-3">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-emerald-200/90">
                Engagedly Employee
            </div>
            <h4 className="mt-2 font-display text-3xl md:text-4xl text-white">
                Empathy map<span className="text-[var(--accent)]">.</span>
            </h4>
            <p className="mt-2 font-sans-brand text-[13.5px] md:text-[14.5px] leading-[1.6] text-white/70">
                Says, thinks, does and feels, captured from the co-design workshop
                around the current Engagedly home page. The four quadrants around
                Wendy surface the friction and expectations that shaped every
                widget decision in the redesign.
            </p>
        </header>

        <div className="relative p-6 md:p-10 pt-2 md:pt-4">
            {/* Faint blueprint grid texture */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                    backgroundSize: '56px 56px',
                }}
            />

            {/* Decorative paper planes with dashed trails */}
            <PaperPlane className="top-4 -left-6 w-16 hidden md:block" rotate={-8} />
            <PaperPlane className="bottom-8 -right-4 w-14 hidden md:block" rotate={172} />

            {/* 3-row grid: TL / TR, persona, BL / BR */}
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-center">
                <CornerNote q={empathy.says} align="left" />
                {/* Central persona, spans the middle column and both rows */}
                <div className="row-span-2 flex flex-col items-center gap-3 md:mx-4">
                    <div
                        className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-[3px]"
                        style={{
                            borderColor: 'rgba(255,255,255,0.85)',
                            boxShadow:
                                '0 0 0 8px rgba(255,255,255,0.06), 0 20px 40px rgba(0,0,0,0.45)',
                        }}
                    >
                        <img
                            src={WENDY_AVATAR}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 8, rotate: -2 }}
                        whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="relative px-5 py-2 rounded-lg font-display text-white text-lg md:text-xl whitespace-nowrap"
                        style={{
                            background:
                                'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            boxShadow: '0 6px 18px rgba(16, 185, 129, 0.35)',
                        }}
                    >
                        Meet Wendy.
                        <span
                            aria-hidden
                            className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm"
                            style={{ background: '#047857' }}
                        />
                        <span
                            aria-hidden
                            className="absolute -right-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm"
                            style={{ background: '#047857' }}
                        />
                    </motion.div>
                </div>
                <CornerNote q={empathy.thinks} align="right" />
                <CornerNote q={empathy.feels} align="left" />
                <CornerNote q={empathy.does} align="right" />
            </div>
        </div>
    </motion.figure>
);

export default EngagedlyEmpathyMap;
